import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { apiRequest } from '../services/apiInterceptor';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAuthenticated, user } = useAuth();

  // Check subscription status
  const checkSubscriptionStatus = async () => {
    if (!isAuthenticated || !user) {
      setSubscription(null);
      setHasActiveSubscription(false);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiRequest('/api/usersubscription/current');
      
      if (response) {
        setSubscription(response);
        setHasActiveSubscription(true);
        // console.log('User has active subscription:', response);
      } else {
        setSubscription(null);
        setHasActiveSubscription(false);
      }
    } catch (error) {
      // console.log('Subscription check error:', error);
      
      // If 404, user doesn't have an active subscription
      if (error.status === 404 || error.message?.includes('404')) {
        setSubscription(null);
        setHasActiveSubscription(false);
        // console.log('User does not have an active subscription');

        // Do not navigate here; allow route guards (ProtectedRoute) to decide
        // whether to redirect based on the current route's requirements.
      } else {
        // Other errors
        setError(error.message || 'Failed to check subscription status');
        setHasActiveSubscription(false);
        setSubscription(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Poll for an updated subscription after returning from Stripe checkout.
  // The webhook may take a few seconds to mark the new subscription as current.
  const pollForSubscriptionUpdate = async (expectedSubId) => {
    const MAX_ATTEMPTS = 10;
    const INTERVAL_MS = 3000;
    console.log(`[SubscriptionContext] Polling for subscription update (expected id: ${expectedSubId})`);

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      await new Promise(r => setTimeout(r, INTERVAL_MS));
      try {
        const res = await apiRequest('/api/usersubscription/current');
        console.log(`[SubscriptionContext] Poll attempt ${attempt}/${MAX_ATTEMPTS}:`, res);
        if (res && String(res.id) === String(expectedSubId)) {
          console.log('[SubscriptionContext] Subscription updated to paid plan!');
          setSubscription(res);
          setHasActiveSubscription(true);
          setIsLoading(false);
          return;
        }
      } catch (err) {
        console.warn(`[SubscriptionContext] Poll attempt ${attempt} error:`, err);
      }
    }
    // Fallback: just load whatever is current
    console.log('[SubscriptionContext] Polling timed out – falling back to current subscription');
    await checkSubscriptionStatus();
  };

  // Check subscription when user authentication changes.
  // After a Stripe payment redirect the pendingSubscription flag triggers polling
  // so the UI waits for the webhook to finalize the new subscription.
  useEffect(() => {
    if (isAuthenticated && user) {
      // Clean up any stale firsttimelogin flags from before this fix
      localStorage.removeItem('firsttimelogin');

      const pendingSub = sessionStorage.getItem('pendingSubscription');
      const pendingSubId = sessionStorage.getItem('pendingSubscriptionId');
      if (pendingSub === 'true' && pendingSubId) {
        sessionStorage.removeItem('pendingSubscription');
        sessionStorage.removeItem('pendingSubscriptionId');
        console.log('[SubscriptionContext] Detected return from Stripe – starting poll for subscription', pendingSubId);
        pollForSubscriptionUpdate(pendingSubId);
        return;
      }
      // Normal flow – just check once
      checkSubscriptionStatus();
    } else {
      setSubscription(null);
      setHasActiveSubscription(false);
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  // Refresh subscription status - enhanced version
  const refreshSubscription = async () => {
    // console.log('Refreshing subscription status...');
    await checkSubscriptionStatus();
  };

  const value = {
    subscription,
    hasActiveSubscription,
    isLoading,
    error,
    refreshSubscription,
    checkSubscriptionStatus,
    // Alias for backward compatibility
    currentSubscription: subscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
